"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";

import type { OrgProfileModel } from "@/api";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface WorkAccessLevelBadgeDropdownProps {
  value: string | null | undefined;
  options: OrgProfileModel[];
  onValueChange: (value: string | null) => void;
  disabled?: boolean;
  /** Smaller badge and chevron for compact headers */
  compact?: boolean;
}

function getBadgeClassName(accessLevelName: string | null | undefined): string {
  const name = accessLevelName?.toLowerCase() || "";

  if (name === "private") {
    return "bg-sky-500 text-white border-sky-500 hover:bg-sky-600";
  }
  if (name === "public") {
    return "bg-green-500 text-white border-green-500 hover:bg-green-600";
  }
  if (name === "team") {
    return "bg-white text-black border-border hover:bg-gray-50";
  }

  // Default styling for unknown access levels
  return "text-foreground border-border";
}

export function WorkAccessLevelBadgeDropdown({
  value,
  options,
  onValueChange,
  disabled = false,
  compact = false,
}: Readonly<WorkAccessLevelBadgeDropdownProps>) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedOption = options.find((option) => option.id === value);
  const displayValue = selectedOption?.name || "Select Access Level";
  const badgeClassName = getBadgeClassName(selectedOption?.name);

  const handleSelect = (optionId: string | null) => {
    onValueChange(optionId);
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type="button"
          className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 focus:outline-none"
          aria-label="Select access level"
        >
          <Badge
            variant="outline"
            className={cn(
              "cursor-pointer border font-normal",
              compact
                ? "px-1.5 py-0 text-[10px] leading-tight"
                : "px-2 py-1 text-xs",
              badgeClassName,
            )}
          >
            {displayValue}
            <ChevronDown
              className={cn("ml-0.5 shrink-0", compact ? "size-2.5" : "size-3")}
            />
          </Badge>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {options.map((option) => (
          <DropdownMenuItem
            key={option.id}
            onClick={() => handleSelect(option.id || null)}
            className="cursor-pointer"
          >
            {option.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
